import React, { useMemo, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Select, Card, Breadcrumb, Table, Tabs, Checkbox } from 'antd';
import { ArrowLeftOutlined, HomeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  FindLocationAssistantReportShiftsResult,
  FindScheduleByIdResult,
  ListEmployeesResult,
  ListLocationsResultDataItem,
  UpdateScheduleShiftDto,
  ToggleScheduleDateIsWaitingForArrangeDto,
} from '../../utils/api-client';
import {
  chunk,
  find,
  findIndex,
  groupBy,
  keyBy,
  map,
  mapValues,
  orderBy,
  times,
} from 'lodash';
import {
  COOPERATION_STATE,
  COOPERATION_STATE_LABEL,
} from '../../constants/cooperation-state';
import { UseMutateAsyncFunction } from 'react-query';
import {
  useCreateToolBagOwner,
  useExportSchedule,
  useToolBagOwners,
} from '../../utils/api-hooks/pt-schedule.hook';
import html2canvas from 'html2canvas';

function hexToRgb(hex: string) {
  hex = hex.replace('#', '');
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('');
  }
  const bigint = parseInt(hex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return [r, g, b];
}

function getLuminance(r: number, g: number, b: number) {
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

function getTextColorFromHexBg(hex: string) {
  const [r, g, b] = hexToRgb(hex);
  const luminance = getLuminance(r, g, b);
  return luminance > 186 ? '#000000' : '#FFFFFF'; // 背景亮就用黑字，背景暗就用白字
}

type PTAssignmentComponentProps = {
  schedule: FindScheduleByIdResult | undefined;
  locations: ListLocationsResultDataItem[] | undefined;
  employees: ListEmployeesResult['data'];
  reportShifts: FindLocationAssistantReportShiftsResult['data'];
  updateShift: UseMutateAsyncFunction<
    unknown,
    unknown,
    { id: string } & UpdateScheduleShiftDto
  >;
  toggleWaitingForArrange: UseMutateAsyncFunction<
    unknown,
    unknown,
    { scheduleId: string } & ToggleScheduleDateIsWaitingForArrangeDto
  >;
  isLoading: boolean;
};

export const PTAssignmentComponent: React.FC<PTAssignmentComponentProps> = ({
  employees = [],
  isLoading,
  locations,
  reportShifts,
  schedule,
  updateShift,
  toggleWaitingForArrange,
}) => {
  const tableRef = useRef(null);
  const { scheduleId } = useParams<{ scheduleId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>('view');
  const [showHolidays, setShowHolidays] = useState<boolean>(false);
  const { mutateAsync: createToolBagOwner } = useCreateToolBagOwner();
  const { isLoading: isExporting } = useExportSchedule();
  const { data: { data: toolBagOwners = [] } = {} } = useToolBagOwners({
    params: [schedule?.startTime, schedule?.endTime],
    enabled: !!schedule,
  });

  const employeeDict = useMemo(() => keyBy(employees, 'id'), [employees]);

  const dateRange = useMemo(() => {
    if (!schedule?.startTime || !schedule?.endTime) return [];

    const dayCount =
      dayjs(schedule.endTime).diff(dayjs(schedule.startTime), 'day') + 1;

    return times(dayCount, (index) => {
      const date = dayjs(schedule.startTime)
        .locale('zh-tw')
        .startOf('day')
        .add(index, 'day');

      return {
        date,
        dateString: date.toISOString(),
        isWeekend: date.day() === 0 || date.day() === 6,
        formattedDate: date.format('MM/DD(dd)'),
      };
    });
  }, [schedule?.startTime, schedule?.endTime]);

  const shiftsByEmployeeAndTime = useMemo(() => {
    if (!schedule?.shifts) return {};

    return mapValues(groupBy(schedule.shifts, 'employeeId'), (employeeShifts) =>
      keyBy(employeeShifts, 'time')
    );
  }, [schedule?.shifts]);

  const shiftsByLocationAndTime = useMemo(() => {
    if (!schedule?.shifts) return {};

    return mapValues(groupBy(schedule.shifts, 'locationId'), (locationShifts) =>
      groupBy(locationShifts, 'time')
    );
  }, [schedule?.shifts]);

  const reportStatusMap = useMemo(() => {
    const map = new Map();

    reportShifts?.forEach((shift) => {
      const key = `${shift.employeeId}_${shift.time}`;
      map.set(key, shift.status);
    });

    return map;
  }, [reportShifts]);

  const getReportStatus = useCallback(
    (employeeId: string, time: string) => {
      const key = `${employeeId}_${time}`;
      return reportStatusMap.get(key);
    },
    [reportStatusMap]
  );

  const getShiftsByLocationAndTime = useCallback(
    (locationId: string, time: string) => {
      return shiftsByLocationAndTime[locationId]?.[time] || [];
    },
    [shiftsByLocationAndTime]
  );

  const handleLocationShiftsUpdate = useCallback(
    (locationId: string, time: string, newEmployeeIds: string[]) => {
      const locationShifts = getShiftsByLocationAndTime(locationId, time);
      const currentEmployeeIds = locationShifts.map(
        (shift) => shift.employeeId
      );

      const employeeIdsToRemove = locationShifts
        .filter((shift) => !newEmployeeIds.includes(shift.employeeId))
        .map((shift) => shift.employeeId);

      const employeeIdsToAdd = newEmployeeIds.filter(
        (employeeId) => !currentEmployeeIds.includes(employeeId)
      );

      const updates: any[] = [];

      employeeIdsToRemove.forEach((employeeId) => {
        updates.push({
          id: scheduleId!,
          employeeId,
          locationId: undefined,
          time: time,
        });
      });

      employeeIdsToAdd.forEach((employeeId) => {
        updates.push({
          id: scheduleId!,
          employeeId,
          locationId: locationId,
          time: time,
        });
      });

      updates.forEach((update) => updateShift(update));
    },
    [scheduleId, getShiftsByLocationAndTime, updateShift]
  );

  const handleEmployeeShiftUpdate = useCallback(
    (employeeId: string, time: string, locationId: string | undefined) => {
      updateShift({
        id: scheduleId!,
        employeeId,
        locationId,
        time,
      });
    },
    [scheduleId, updateShift]
  );

  const handleBack = useCallback(() => {
    navigate('/schedules/pt');
  }, [navigate]);

  const getIsWaitingForArrange = useCallback(
    (dateString: string, locationId: string) => {
      const dateItem = schedule?.dates?.find(
        (item) => item.time === dateString && item.locationId === locationId
      );
      return dateItem?.isWaitingForArrange || false;
    },
    [schedule?.dates]
  );

  const handleNeedMoreStaffChange = useCallback(
    (locationId: string, dateString: string, checked: boolean) => {
      if (scheduleId) {
        toggleWaitingForArrange({
          scheduleId,
          time: dateString,
          locationId,
        });
      }
    },
    [scheduleId, toggleWaitingForArrange]
  );

  const employeeDataSource = useMemo(() => {
    return orderBy(
      map(employees, (employee) => ({
        onBoardingTime: employee.onBoardingTime,
        employeeId: employee.id,
        employeeName: employee.name,
        employeeIdNumber: employee.idNumber,
        ...mapValues(
          shiftsByEmployeeAndTime[employee.id] || {},
          (shift) => shift.locationId
        ),
      })),
      ['onBoardingTime'],
      ['asc']
    );
  }, [employees, shiftsByEmployeeAndTime]);

  const locationDataSource = useMemo(() => {
    const preferredOrder = [
      '信義區',
      '內湖區',
      '南港區',
      '松山區',
      '大安區',
      null,
    ];
    return orderBy(
      map(locations, (location) => ({
        locationId: location.id,
        locationName: location.name,
        locationDistrict: location.district,
        locationIdNumber: location.idNumber,
        locationStatus: location.status,
        locationNickname: location.nickname,
        locationBackgroundColor: location.backgroundColor,
        locationMeetUpPoint: location.meetUpPoint,
        isNeedToolBag: location.isLocationAssistantNeedToolsBag,
      })),
      [
        (item) => {
          const index = findIndex(
            preferredOrder,
            (district) => item.locationDistrict === district
          );
          return index === -1 ? preferredOrder.length : index;
        },
        'locationStatus',
        'locationIdNumber',
      ],
      ['asc', 'asc', 'asc']
    );
  }, [locations]);

  const renderEmployeeOptions = useCallback(
    (date: string, locationId: string) => {
      return orderBy(
        map(employees, (employee) => {
          const reportStatus = getReportStatus(employee.id, date);
          const isAvailable = reportStatus === 'AVAILABLE';

          return {
            name: `${employee.idNumber}${employee.name}`,
            isAvailable,
            label: (
              <div className="flex items-center">
                {isAvailable && (
                  <span
                    className="flex-shrink-0 w-3 h-3 mr-2 rounded-full"
                    style={{ backgroundColor: 'lightgreen' }}
                  />
                )}
                <span className="overflow-hidden text-ellipsis">
                  {employee.idNumber} - {employee.name}
                </span>
              </div>
            ),
            value: employee.id,
          };
        }),
        ['isAvailable', 'name'],
        ['desc', 'asc']
      );
    },
    [employees, getReportStatus]
  );

  const renderLocationOptions = useCallback(() => {
    return map(locations, (location) => ({
      name: `${location.idNumber}${location.nickname || location.name}`,
      label: (
        <div className="flex items-center">
          <span
            className="flex-shrink-0 w-3 h-3 rounded-full"
            style={{
              ...(location.backgroundColor && {
                backgroundColor: location.backgroundColor,
              }),
            }}
          />
          <span
            className={`ml-2 flex-shrink text-ellipsis overflow-hidden ${
              location.status === COOPERATION_STATE.INACTIVE
                ? 'text-red-500'
                : ''
            }`}
          >
            {location.idNumber} - {location.nickname || location.name}
            {location.status === COOPERATION_STATE.INACTIVE &&
              `(${COOPERATION_STATE_LABEL[location.status]})`}
          </span>
        </div>
      ),
      value: location.id,
    }));
  }, [locations]);

  const onExportButtonClick = () => {
    const tRef = (tableRef.current as any).childNodes[0].querySelector('table');
    html2canvas(tRef).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = imgData;
      link.download = `班表_${dayjs(schedule?.startTime).format(
        'YYYY-MM-DD'
      )}至${dayjs(schedule?.endTime).format('YYYY-MM-DD')}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  return (
    <div className="flex flex-col items-stretch gap-4">
      <Breadcrumb
        items={[
          {
            title: <HomeOutlined />,
            href: '/',
          },
          {
            title: 'PT班表管理',
            href: '/schedules/pt',
          },
          {
            title: '班表日程表',
          },
        ]}
      />
      <div className="flex items-center justify-between">
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          返回班表列表
        </Button>
        <span className="text-lg font-bold">
          班表日程表: {dayjs(schedule?.startTime).format('YYYY-MM-DD')} 至{' '}
          {dayjs(schedule?.endTime).format('YYYY-MM-DD')}
        </span>
      </div>

      <Card>
        <div>
          <Checkbox
            onChange={(e) => setShowHolidays(e.target.checked)}
            checked={showHolidays}
          >
            顯示假日
          </Checkbox>
        </div>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          type="card"
          destroyInactiveTabPane
          items={[
            {
              label: '員工視圖',
              key: 'employee',
              children: (
                <Table
                  rowKey="employeeId"
                  pagination={false}
                  scroll={{ x: 'max-content' }}
                  bordered
                  size="small"
                  loading={isLoading}
                  columns={[
                    {
                      title: '員工',
                      dataIndex: 'employeeName',
                      fixed: 'left' as const,
                      width: 140,
                      render: (text: string, record) => (
                        <span>
                          {record.employeeIdNumber} - {record.employeeName}
                        </span>
                      ),
                    },
                    ...dateRange.map(
                      ({ dateString, formattedDate, isWeekend }) => ({
                        align: 'center' as any,
                        hidden: !showHolidays && isWeekend,
                        title: (
                          <span
                            className={`${isWeekend ? 'text-red-500' : ''}`}
                          >
                            {formattedDate}
                          </span>
                        ),
                        dataIndex: dateString,
                        width: 160,
                        render: (text: string, record: any) => {
                          const reportStatus = getReportStatus(
                            record.employeeId,
                            dateString
                          );
                          const isAvailable = reportStatus === 'AVAILABLE';

                          return (
                            <div className="flex items-center">
                              {isAvailable && (
                                <span
                                  className="flex-shrink-0 w-3 h-3 mr-2 rounded-full"
                                  style={{
                                    backgroundColor: 'lightgreen',
                                  }}
                                />
                              )}
                              <Select
                                value={record[dateString] || undefined}
                                allowClear
                                showSearch
                                optionFilterProp="name"
                                style={{
                                  width: '100%',
                                  backgroundColor: isAvailable
                                    ? '#e6f7e6'
                                    : 'transparent',
                                }}
                                onChange={(value) => {
                                  handleEmployeeShiftUpdate(
                                    record.employeeId,
                                    dateString,
                                    value
                                  );
                                }}
                                options={renderLocationOptions()}
                              />
                            </div>
                          );
                        },
                      })
                    ),
                  ]}
                  dataSource={employeeDataSource}
                />
              ),
            },
            {
              label: '地點日曆視圖',
              key: 'calendar',
              children: (
                <Table
                  pagination={false}
                  scroll={{ x: 'max-content' }}
                  rowClassName={(_, index) =>
                    _.locationStatus === COOPERATION_STATE.INACTIVE
                      ? 'bg-gray-200'
                      : ''
                  }
                  bordered
                  size="small"
                  rowKey="locationId"
                  loading={isLoading}
                  dataSource={locationDataSource}
                  columns={[
                    {
                      title: '服務大樓',
                      dataIndex: 'locationName',
                      fixed: 'left' as const,
                      width: 140,
                      className: 'bg-red-100',
                      render: (text: string, record) => (
                        <div className="flex items-center">
                          {record.locationBackgroundColor && (
                            <span
                              className="flex-shrink-0 w-3 h-3 mr-2 rounded-full"
                              style={{
                                backgroundColor: record.locationBackgroundColor,
                              }}
                            />
                          )}
                          <span
                            className={
                              record.locationStatus ===
                              COOPERATION_STATE.INACTIVE
                                ? 'text-red-500'
                                : ''
                            }
                          >
                            {record.locationIdNumber} -{' '}
                            {record.locationNickname || record.locationName}
                            {record.locationStatus ===
                              COOPERATION_STATE.INACTIVE && (
                              <span>
                                (
                                {COOPERATION_STATE_LABEL[record.locationStatus]}
                                )
                              </span>
                            )}
                          </span>
                        </div>
                      ),
                    },
                    ...dateRange.map(
                      ({ dateString, formattedDate, isWeekend }) => ({
                        align: 'center' as any,
                        hidden: !showHolidays && isWeekend,
                        title: (
                          <span
                            className={`${isWeekend ? 'text-red-500' : ''}`}
                          >
                            {formattedDate}
                          </span>
                        ),
                        dataIndex: dateString,
                        width: 140,
                        render: (
                          shifts: FindScheduleByIdResult['shifts'],
                          record: any
                        ) => {
                          const locationShifts = getShiftsByLocationAndTime(
                            record.locationId,
                            dateString
                          );
                          const currentEmployeeIds = map(
                            orderBy(
                              map(locationShifts, (s) => ({
                                ...employeeDict[s.employeeId],
                                ...s,
                              })),
                              ['employeeIdNumber'],
                              ['asc']
                            ),
                            (shift) => shift.employeeId
                          );

                          const isNeedMoreStaff = getIsWaitingForArrange(
                            dateString,
                            record.locationId
                          );

                          return (
                            <div
                              className="flex flex-col items-stretch gap-2"
                              style={{
                                backgroundColor: isNeedMoreStaff
                                  ? '#fce7f3'
                                  : 'transparent',
                              }}
                            >
                              <div className="flex items-center mb-2 gap-2">
                                <Checkbox
                                  checked={isNeedMoreStaff}
                                  onChange={(e) =>
                                    handleNeedMoreStaffChange(
                                      record.locationId,
                                      dateString,
                                      e.target.checked
                                    )
                                  }
                                />
                                <span
                                  className="text-xs text-gray-600 cursor-pointer select-none"
                                  onClick={() =>
                                    handleNeedMoreStaffChange(
                                      record.locationId,
                                      dateString,
                                      !isNeedMoreStaff
                                    )
                                  }
                                >
                                  需要補人
                                </span>
                              </div>
                              <Select
                                mode="multiple"
                                value={currentEmployeeIds}
                                allowClear
                                showSearch
                                optionFilterProp="name"
                                onChange={(values: string[]) => {
                                  handleLocationShiftsUpdate(
                                    record.locationId,
                                    dateString,
                                    values
                                  );
                                }}
                                options={renderEmployeeOptions(
                                  dateString,
                                  record.locationId
                                )}
                              />
                              {record.isNeedToolBag && (
                                <div className="flex items-center gap-2">
                                  <span className="shrink-0">👜</span>
                                  <Select
                                    value={
                                      find(
                                        toolBagOwners,
                                        (owner) =>
                                          owner.locationId ===
                                            record.locationId &&
                                          owner.time === dateString
                                      )?.employeeId
                                    }
                                    className="grow"
                                    onChange={(value) => {
                                      createToolBagOwner({
                                        employeeId: value,
                                        locationId: record.locationId,
                                        time: dateString,
                                      });
                                    }}
                                    options={map(currentEmployeeIds, (id) => ({
                                      label: employeeDict[id]?.name,
                                      value: id,
                                    }))}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        },
                      })
                    ),
                  ]}
                />
              ),
            },
            {
              label: '檢視班表',
              key: 'view',
              children: (
                <>
                  <div>
                    <Button
                      type="primary"
                      onClick={onExportButtonClick}
                      loading={isExporting}
                    >
                      匯出班表
                    </Button>
                  </div>
                  <Table
                    ref={tableRef}
                    pagination={false}
                    bordered
                    size="small"
                    rowKey="locationId"
                    loading={isLoading}
                    dataSource={locationDataSource}
                    scroll={{ x: 'max-content' }}
                    columns={[
                      {
                        title: '服務大樓',
                        dataIndex: 'locationName',
                        fixed: 'left' as const,
                        width: 160,
                        onCell: (record) => {
                          const { locationBackgroundColor } = record;

                          if (!locationBackgroundColor) return {};

                          return {
                            style: {
                              backgroundColor:
                                locationBackgroundColor || undefined,
                              color: getTextColorFromHexBg(
                                locationBackgroundColor
                              ),
                            },
                          };
                        },
                        render: (text: string, record) => (
                          <span>
                            {record.locationIdNumber} -{' '}
                            {record.locationNickname || record.locationName}
                          </span>
                        ),
                      },
                      {
                        align: 'center',
                        title: '服務區域',
                        dataIndex: 'locationDistrict',
                        width: 100,
                      },
                      {
                        title: '集合地點',
                        width: 140,
                        dataIndex: 'locationMeetUpPoint',
                      },
                      ...map(
                        dateRange,
                        ({ dateString, formattedDate, isWeekend }) => ({
                          align: 'center' as any,
                          hidden: !showHolidays && isWeekend,
                          title: (
                            <span
                              className={`${isWeekend ? 'text-red-500' : ''}`}
                            >
                              {formattedDate}
                            </span>
                          ),
                          dataIndex: dateString,
                          width: 160,
                          onCell: (record: any) => ({
                            style: {
                              padding: 0,
                              verticalAlign: 'top',
                            },
                          }),
                          render: (
                            shifts: FindScheduleByIdResult['shifts'],
                            record: any
                          ) => {
                            const locationShifts = getShiftsByLocationAndTime(
                              record.locationId,
                              dateString
                            );
                            const currentEmployeeIds = map(
                              orderBy(
                                map(locationShifts, (s) => ({
                                  ...employeeDict[s.employeeId],
                                  ...s,
                                })),
                                ['employeeIdNumber'],
                                ['asc']
                              ),
                              (shift) => shift.employeeId
                            );

                            return (
                              <table className="!border-collapse">
                                {map(
                                  chunk(currentEmployeeIds, 2),
                                  (row, index) => (
                                    <tr key={index}>
                                      {map(row, (id) => {
                                        const isBagOwner = !!find(
                                          toolBagOwners,
                                          (owner) =>
                                            owner.locationId ===
                                              record.locationId &&
                                            owner.time === dateString &&
                                            owner.employeeId === id
                                        );
                                        return (
                                          <td
                                            key={id}
                                            className="border border-black border-solid"
                                            style={{
                                              width: '50%',
                                              padding: '4px',
                                            }}
                                          >
                                            {`${employeeDict[id]?.name}${isBagOwner ? ' 👜' : ''}`}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  )
                                )}
                              </table>
                            );
                          },
                        })
                      ),
                    ]}
                  />
                </>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};
